/**
 * Performance-review guidance (Oct 2026): the texts come from `config.reviewGuidance` merged over
 * the shared drafts; "seen" lives on the user doc (`reviewGuidanceSeen`, written by
 * `reviewMarkGuidanceSeen`), so a screen shows once per person / per cycle on every device.
 */
import { useCallback, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useProfile } from '../profile/ProfileProvider';
import {
  CALLABLES,
  resolveReviewGuidance,
  reviewGuidanceSeenKey,
  type ReviewGuidance,
  type ReviewGuidanceKind,
  type ReviewMarkGuidanceSeenInput,
  type ReviewMarkGuidanceSeenResponse,
} from '@daaily/shared';

const callMarkSeen = httpsCallable<ReviewMarkGuidanceSeenInput, ReviewMarkGuidanceSeenResponse>(
  functions,
  CALLABLES.REVIEW_MARK_GUIDANCE_SEEN,
);

/** The resolved texts (HR edits over the drafts). */
export function useReviewGuidance(): ReviewGuidance {
  const { state } = useProfile();
  const cfg = state.status === 'ready' ? state.config.reviewGuidance : null;
  return useMemo(() => resolveReviewGuidance(cfg), [cfg]);
}

/**
 * Whether one guidance screen is still due for the signed-in person, and a `markSeen` that hides
 * it at once (local) and records it on the user doc. `ready` is false until the profile loaded,
 * so callers don't flash a screen that was already seen.
 */
export function useGuidanceSeen(kind: ReviewGuidanceKind, cycleId?: string): { ready: boolean; due: boolean; markSeen: () => void } {
  const { state } = useProfile();
  const key = reviewGuidanceSeenKey(kind, cycleId);
  const [local, setLocal] = useState<Record<string, true>>({});
  const ready = state.status === 'ready';
  const seenOnDoc = ready && !!state.user.reviewGuidanceSeen?.[key];
  const due = ready && !seenOnDoc && !local[key];
  const markSeen = useCallback(() => {
    setLocal((m) => ({ ...m, [key]: true }));
    callMarkSeen({ key }).catch(() => { /* best-effort: shows again next time */ });
  }, [key]);
  return { ready, due, markSeen };
}
